import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { http, TYPES, typeLabel, waLink } from "../lib/api";
import { useFacets } from "../components/home/PartFinder";
import { ProductCard } from "../components/site/ProductCard";
import { Eyebrow } from "../components/site/Reveal";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`w-full rounded-full border px-4 py-2 text-left text-sm transition-colors ${active ? "border-[#E11D48] bg-[#E11D48] text-white" : "border-white/10 text-slate-300 hover:border-white/40"}`}
  >
    {children}
  </button>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const facets = useFacets();
  const [items, setItems] = useState(null);
  const make = params.get("make") || "";
  const model = params.get("model") || "";
  const type = params.get("type") || "";
  const q = params.get("q") || "";
  const year = params.get("year") || "";
  const reg = params.get("reg") || "";
  const colour = params.get("colour") || "";
  const [term, setTerm] = useState(q);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    if (k === "make") { p.delete("model"); p.delete("year"); p.delete("reg"); p.delete("colour"); }
    setParams(p);
  };

  useEffect(() => {
    setItems(null);
    http
      .get("/products", { params: { make, model, type, q, ...(year ? { year } : {}) } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [make, model, type, q, year]);

  const models = facets.find((f) => f.make === make)?.models ?? [];
  const active = [make, model, year && `Year ${year}`, type && typeLabel(type), q].filter(Boolean);

  return (
    <main data-testid="shop-page" className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-8 lg:px-12">
      <Eyebrow>Catalogue</Eyebrow>
      <h1 className="mt-4 font-display text-6xl font-black uppercase leading-[0.85] tracking-tight sm:text-8xl">Shop parts</h1>
      {reg && (
        <div data-testid="shop-reg-banner" className="mt-8 flex flex-wrap items-center gap-4 border border-[#FFD500]/40 bg-[#FFD500]/[0.06] p-4">
          <span className="rounded border-2 border-black bg-[#FFD500] px-3 py-1 font-display text-2xl font-black tracking-widest text-black">{reg}</span>
          <p className="text-sm text-slate-200">Showing parts for your {[year, colour && colour.toLowerCase(), make].filter(Boolean).join(" ")}. Always match the part number before fitting.</p>
          <a data-testid="shop-reg-whatsapp" href={waLink(`Hi CBLS AIRBAGS, which airbags fit my car? Reg: ${reg}`)} target="_blank" rel="noreferrer" className="ml-auto font-mono text-xs uppercase tracking-widest text-emerald-400 hover:underline">Ask us to confirm fit →</a>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); update("q", term.trim()); }} className="mt-10 flex border border-white/10 bg-[#121820]">
        <Search className="ml-5 h-5 w-5 self-center text-slate-400" />
        <input data-testid="shop-search-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search part number, make, model..." className="h-14 flex-1 bg-transparent px-4 text-base outline-none placeholder:text-slate-500" />
        <button data-testid="shop-search-button" className="bg-[#E11D48] px-6 font-semibold hover:bg-[#F43F5E]">Search</button>
      </form>
      {active.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {active.map((a) => <span key={a} className="rounded-full bg-white/10 px-3 py-1 font-mono text-xs">{a}</span>)}
          <button data-testid="shop-clear-filters" onClick={() => { setTerm(""); setParams({}); }} className="flex items-center gap-1 font-mono text-xs text-[#F43F5E]"><X className="h-3 w-3" /> Clear</button>
        </div>
      )}
      <div className="mt-12 grid gap-12 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-8">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Airbag type</p>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
              <Chip testid="filter-type-all" active={!type} onClick={() => update("type", "")}>All types</Chip>
              {TYPES.map((t) => <Chip key={t.value} testid={`filter-type-${t.value}`} active={type === t.value} onClick={() => update("type", t.value)}>{t.label}</Chip>)}
            </div>
          </div>
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Make</p>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
              <Chip testid="filter-make-all" active={!make} onClick={() => update("make", "")}>All makes</Chip>
              {facets.map((f) => <Chip key={f.make} testid={`filter-make-${f.make}`} active={make === f.make} onClick={() => update("make", f.make)}>{f.make} <span className="text-slate-400">({f.count})</span></Chip>)}
            </div>
          </div>
          {models.length > 0 && (
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">Model</p>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
                <Chip testid="filter-model-all" active={!model} onClick={() => update("model", "")}>All models</Chip>
                {models.map((m) => <Chip key={m} testid={`filter-model-${m}`} active={model === m} onClick={() => update("model", m)}>{m}</Chip>)}
              </div>
            </div>
          )}
        </aside>
        <section>
          <p data-testid="shop-result-count" className="mb-6 font-mono text-xs uppercase tracking-widest text-slate-400">
            {items ? `${items.length} part${items.length === 1 ? "" : "s"}` : "Loading..."}
          </p>
          {items && items.length === 0 && (
            <div data-testid="shop-empty" className="border border-dashed border-white/15 p-12 text-center text-slate-300">
              No parts match yet. Message us — we may have it in stock but not listed.
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 xl:grid-cols-3">
            {(items ?? []).map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
          </div>
        </section>
      </div>
    </main>
  );
}
